/**
 * Google Maps style definitions with mapTypeId constants.
 * Defines available styles for 2D and 3D map modes.
 */

import type { ViewMode } from './map-setup';

/** Map style configuration */
export interface MapStyle {
  id: string;
  label: string;
  /** Whether this style is available in 3D mode */
  available3d: boolean;
}

/** Predefined map styles */
export const MAP_STYLES: ReadonlyArray<MapStyle> = [
  { id: 'dark', label: 'Dark', available3d: true },
  { id: 'satellite', label: 'Satellite', available3d: true },
  { id: 'streets', label: 'Streets', available3d: false },
  { id: 'terrain', label: 'Terrain', available3d: false },
];

/** Map style ID to Google Maps mapTypeId for 2D mode */
const STYLE_TO_MAP_TYPE: ReadonlyMap<string, string> = new Map([
  ['dark', 'roadmap'],
  ['satellite', 'hybrid'],
  ['streets', 'roadmap'],
  ['terrain', 'terrain'],
]);

/** Apply a style to a 2D map */
export function applyStyle2D(map: google.maps.Map, styleId: string): void {
  const mapTypeId = STYLE_TO_MAP_TYPE.get(styleId) ?? 'roadmap';
  map.setMapTypeId(mapTypeId);
}

/** Apply a style to a 3D map (3D always renders photorealistic satellite) */
export function applyStyle3D(
  _map3d: google.maps.maps3d.Map3DElement,
  _styleId: string,
): void {
  /* 3D tiles are always photorealistic satellite — no style switching needed */
}

/** Get styles available for the current view mode */
export function getAvailableStyles(viewMode: ViewMode): ReadonlyArray<MapStyle> {
  if (viewMode === '3d') {
    return MAP_STYLES.filter((s) => s.available3d);
  }
  return MAP_STYLES;
}
